'use client';

import { useState } from 'react';
import { Alert, Button, Dialog, TextArea } from '@/shared/components/ui';
import { FeatherAlertCircle, FeatherInfo, FeatherRotateCcw } from '@subframe/core';

interface ModalReaberturaNegociacaoProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  negociacaoNome: string;
  onReabrir: (manterDados: boolean, justificativa: string) => Promise<void>;
  isReabrindo?: boolean;
}

export function ModalReaberturaNegociacao({
  open,
  onOpenChange,
  negociacaoNome,
  onReabrir,
  isReabrindo = false,
}: ModalReaberturaNegociacaoProps) {
  const [justificativa, setJustificativa] = useState('');
  const [erro, setErro] = useState('');

  const handleConfirmar = async () => {
    if (!justificativa.trim()) {
      setErro('A justificativa é obrigatória');
      return;
    }

    try {
      await onReabrir(true, justificativa);
      handleFechar();
    } catch (error) {
      setErro('Erro ao reabrir negociação. Tente novamente.');
      console.error('Erro ao reabrir:', error);
    }
  };

  const handleFechar = () => {
    setJustificativa('');
    setErro('');
    onOpenChange(false);
  };

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={handleFechar}>
      <Dialog.Content className="w-full max-w-2xl p-0">
        <div className="flex w-full flex-col">
          <div className="border-b border-neutral-200 px-6 py-4">
            <div className="flex items-center gap-3">
              <div className="bg-brand-100 flex h-10 w-10 items-center justify-center rounded-lg">
                <FeatherRotateCcw className="text-brand-700 h-5 w-5" />
              </div>
              <h2 className="text-heading-3 font-heading-3 text-default-font">
                Reabrir Negociação
              </h2>
            </div>
            <p className="text-body-sm text-subtext-color mt-1">{negociacaoNome}</p>
          </div>

          <div className="flex flex-col gap-4 px-6 py-4">
            <div className="flex flex-col gap-2">
              <TextArea label="Justificativa para reabertura (obrigatório)" disabled={isReabrindo}>
                <TextArea.Input
                  placeholder="Explique o motivo da reabertura desta negociação..."
                  value={justificativa}
                  onChange={(e) => setJustificativa(e.target.value)}
                  rows={4}
                />
              </TextArea>
            </div>

            <Alert
              variant="brand"
              icon={<FeatherInfo />}
              title="Ao reabrir a negociação:"
              description={
                <ul className="mt-1 ml-4 list-disc space-y-0.5 text-sm">
                  <li>O caso voltará a ficar ativo, com o histórico anterior disponível</li>
                  <li>Se a janela do WhatsApp estiver vencida, envie a mensagem de retomada</li>
                  <li>Aguarde a resposta da pessoa antes de escrever mensagens livres</li>
                  <li>A reabertura será registrada na linha do tempo</li>
                </ul>
              }
            />

            {erro && (
              <Alert
                variant="error"
                icon={<FeatherAlertCircle />}
                description={<p className="text-sm">{erro}</p>}
              />
            )}
          </div>

          <div className="flex items-center justify-end gap-3 border-t border-neutral-200 bg-neutral-50 px-6 py-4">
            <Button variant="neutral-secondary" onClick={handleFechar} disabled={isReabrindo}>
              Cancelar
            </Button>
            <Button
              variant="brand-primary"
              icon={<FeatherRotateCcw />}
              onClick={handleConfirmar}
              disabled={isReabrindo}
              loading={isReabrindo}
            >
              Reabrir Negociação
            </Button>
          </div>
        </div>
      </Dialog.Content>
    </Dialog>
  );
}
